import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '~/lib/api';
import {
  KYC_STATUS_LABEL,
  KYC_STATUS_TONE,
  type Cardholder,
  type KycStatus,
} from '~/lib/cardholders';
import { NewCardholderPanel } from '~/components/cardholders/NewCardholderPanel';

type Status =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; data: Cardholder[] };

/**
 * Lists the tenant's cardholders. The DB + routes keep the historical
 * `customer` naming (see docs/design/2026-10-03-dashboard-kyb-and-customers.md
 * §3 Decision 4) but the UI label is "Cardholders" everywhere.
 */
export function CardholdersPage(): JSX.Element {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [panelOpen, setPanelOpen] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await api<{ data: Cardholder[] }>('/dashboard/customers');
      setStatus({ kind: 'ok', data: res.data });
    } catch (err) {
      setStatus({
        kind: 'error',
        message:
          err instanceof ApiError ? err.message : 'Could not load cardholders',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-10 pt-6 pb-12">
      <div className="mb-5 flex items-end justify-between gap-6">
        <div>
          <h1 className="text-[22px] font-semibold -tracking-[0.015em]">
            Cardholders
          </h1>
          <p className="mt-1 max-w-[56ch] text-[14.5px] text-ink-secondary">
            People or businesses a card is issued to. Create a cardholder first,
            then issue a card against them.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-ink-primary px-3 py-2 text-[14px] font-semibold text-ink-on-dark hover:bg-ink-primary/90"
        >
          <svg
            width="11"
            height="11"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinecap="round"
          >
            <path d="M8 3v10M3 8h10" />
          </svg>
          New cardholder
        </button>
      </div>

      {status.kind === 'loading' && (
        <div className="rounded-lg border border-rule-hairline bg-surface-slip p-6 text-[14.5px] text-ink-secondary">
          Loading…
        </div>
      )}
      {status.kind === 'error' && (
        <div className="rounded-lg border border-stop/20 bg-stop-tint p-4 text-[14px] text-stop">
          {status.message}
        </div>
      )}
      {status.kind === 'ok' && status.data.length === 0 && (
        <div className="rounded-lg border border-rule-hairline bg-surface-slip p-10 text-center">
          <div className="text-[18px] font-semibold text-ink-primary">
            No cardholders yet
          </div>
          <p className="mt-1 text-[14.5px] text-ink-secondary">
            Create your first cardholder above to start issuing cards.
          </p>
        </div>
      )}

      {status.kind === 'ok' && status.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule-hairline bg-surface-slip">
          <div className="hidden grid-cols-[1.4fr_1fr_1.4fr_120px_120px] gap-5 border-b border-rule-hairline px-5 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-watermark md:grid">
            <div>Name</div>
            <div>Ref</div>
            <div>Contact</div>
            <div>KYC</div>
            <div>Created</div>
          </div>
          {status.data.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-1 gap-3 border-b border-rule-hairline px-5 py-4 last:border-b-0 hover:bg-surface-margin/50 md:grid-cols-[1.4fr_1fr_1.4fr_120px_120px] md:gap-5"
            >
              <div>
                <div className="text-[14.5px] font-semibold text-ink-primary">
                  {c.firstName} {c.lastName}
                </div>
                <div className="font-mono mt-0.5 text-[13px] text-ink-watermark">
                  {c.type}
                </div>
              </div>
              <div className="font-mono flex items-center text-[13.5px] text-ink-secondary">
                {c.externalRef}
              </div>
              <div className="flex min-w-0 flex-col justify-center">
                <div className="truncate text-[13.5px] text-ink-primary">
                  {c.email}
                </div>
                <div className="font-mono mt-0.5 text-[13px] text-ink-watermark">
                  {c.phoneNumber}
                </div>
              </div>
              <div className="flex items-center">
                <KycChip status={c.kycStatus} />
              </div>
              <div className="font-mono flex items-center text-[13.5px] text-ink-secondary">
                {formatDate(c.createdAt)}
              </div>
            </div>
          ))}
        </div>
      )}

      <NewCardholderPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        onCreated={() => void load()}
      />
    </div>
  );
}

function KycChip({ status }: { status: KycStatus }): JSX.Element {
  return (
    <span
      className={`font-mono inline-flex items-center rounded-full px-2 py-[3px] text-[11px] font-semibold uppercase tracking-[0.08em] ${KYC_STATUS_TONE[status]}`}
    >
      {KYC_STATUS_LABEL[status]}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}
